import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, Grid, Stack } from '@mui/material';
import { TokenTextField } from './TokenTextField.styles';
import { SelectOption } from 'src/UIKit';
import EventIcon from '@mui/icons-material/Event';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { DATE_FORMAT, DATE_DISPLAY_FORMAT, FormDatePicker } from 'src/components/FormPart/FormDatePicker';
import { DATETIME_FORMAT, DATETIME_DISPLAY_FORMAT, FormDateTimePicker } from 'src/components/FormPart/FormDateTimePicker';
import { normalizeRuleValues } from '../../helpers';
import { useGroupsApi } from '../../api';
import { GroupFormData } from '../../types';

dayjs.extend(utc);

type DateRuleValuesInputProps = {
  form: UseFormReturn<GroupFormData>;
  fieldName: `rules.${number}.values`;
  includeTime: boolean;
};

type DateRuleTab = 'fixed' | 'dynamic';
type CustomDateUnit = 'day' | 'week' | 'month' | 'year';
type CustomDateDirection = 'ago' | 'from_now';
type FixedDateFormData = {
  fixedDateValue: string;
};

const customDateUnitOptions: SelectOption[] = [
  { title: 'day', value: 'day' },
  { title: 'week', value: 'week' },
  { title: 'month', value: 'month' },
  { title: 'year', value: 'year' },
];
const customDateDirectionOptions: SelectOption[] = [
  { title: 'ago', value: 'ago' },
  { title: 'from now', value: 'from_now' },
];

const buildCustomDynamicDateValue = (
  amount: number,
  unit: CustomDateUnit,
  direction: CustomDateDirection
): string => {
  const normalizedAmount = Number.isFinite(amount) && amount > 0 ? Math.floor(amount) : 1;
  const unitTitle = normalizedAmount === 1 ? unit : `${unit}s`;

  return direction === 'ago'
    ? `${normalizedAmount} ${unitTitle} ago`
    : `${normalizedAmount} ${unitTitle} from now`;
};

const getDefaultFixedDateValue = (includeTime: boolean): string => {
  return dayjs().format(includeTime ? DATETIME_FORMAT : DATE_FORMAT);
};

export const DateRuleValuesInput: FC<DateRuleValuesInputProps> = ({
  form,
  fieldName,
  includeTime,
}) => {
  const groupsApi = useGroupsApi();
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const valueControlRef = useRef<HTMLDivElement | null>(null);
  const fixedDateForm = useForm<FixedDateFormData>({
    defaultValues: {
      fixedDateValue: getDefaultFixedDateValue(includeTime),
    },
  });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeTab, setActiveTab] = useState<DateRuleTab>('fixed');
  const [customAmount, setCustomAmount] = useState(1);
  const [customUnit, setCustomUnit] = useState<CustomDateUnit>('day');
  const [customDirection, setCustomDirection] = useState<CustomDateDirection>('ago');
  const [dynamicPreviewDate, setDynamicPreviewDate] = useState<string | null>(null);
  const [isDynamicPreviewLoading, setIsDynamicPreviewLoading] = useState(false);
  const [dynamicPreviewError, setDynamicPreviewError] = useState<string | null>(null);
  const isPopoverOpen = Boolean(anchorEl);

  useEffect(() => {
    fixedDateForm.reset({ fixedDateValue: getDefaultFixedDateValue(includeTime) });
  }, [fixedDateForm, includeTime]);

  const dynamicExpression = useMemo(() => {
    return buildCustomDynamicDateValue(customAmount, customUnit, customDirection);
  }, [customAmount, customDirection, customUnit]);

  const [debouncedDynamicExpression, setDebouncedDynamicExpression] = useState(dynamicExpression);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDynamicExpression(dynamicExpression), 400);
    return () => clearTimeout(timer);
  }, [dynamicExpression]);

  const shouldLoadDynamicPreview =
    activeTab === 'dynamic' && isPopoverOpen;

  useEffect(() => {
    if (!shouldLoadDynamicPreview || !currentCompanyId) {
      setDynamicPreviewDate(null);
      setDynamicPreviewError(null);
      setIsDynamicPreviewLoading(false);
      return;
    }

    let isCancelled = false;
    setIsDynamicPreviewLoading(true);
    setDynamicPreviewError(null);

    groupsApi
      .getGroupDatePreview(currentCompanyId, {
        expression: debouncedDynamicExpression,
      })
      .then(response => {
        if (isCancelled) {
          return;
        }

        setDynamicPreviewDate(response.data.date);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setDynamicPreviewDate(null);
        setDynamicPreviewError('Unable to calculate preview for selected expression');
      })
      .finally(() => {
        if (!isCancelled) {
          setIsDynamicPreviewLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [currentCompanyId, debouncedDynamicExpression, groupsApi, shouldLoadDynamicPreview]);

  const setValues = useCallback(
    (nextValues: readonly string[]) => {
      form.setValue(fieldName, normalizeRuleValues(nextValues), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [fieldName, form],
  );

  const openPopover = useCallback(() => {
    setAnchorEl(valueControlRef.current);
  }, []);

  const addValueFromSelector = useCallback(() => {
    const nextValue =
      activeTab === 'fixed'
        ? fixedDateForm.getValues('fixedDateValue')
        : dynamicExpression;

    if (!nextValue) {
      return;
    }

    const rawValues = form.getValues(fieldName);
    const currentValues = normalizeRuleValues(Array.isArray(rawValues) ? rawValues : []);
    setValues([...currentValues, nextValue]);
    setAnchorEl(null);
  }, [activeTab, dynamicExpression, fieldName, fixedDateForm, form, setValues]);

  return (
    <Controller<GroupFormData, `rules.${number}.values`>
      name={fieldName}
      control={form.control}
      rules={{
        validate: value =>
          normalizeRuleValues(Array.isArray(value) ? value : []).length > 0 ||
          'Value is required',
      }}
      render={({ field, fieldState }) => {
        const currentValues = normalizeRuleValues(Array.isArray(field.value) ? field.value : []);

        return (
          <Box>
            <TokenTextField
              ref={valueControlRef}
              hasTokens={currentValues.length > 0}
              label="Values"
              margin="dense"
              required
              fullWidth
              value=""
              onBlur={field.onBlur}
              onClick={openPopover}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              placeholder={currentValues.length ? undefined : 'No values selected'}
              inputProps={{ readOnly: true }}
              InputProps={{
                startAdornment: currentValues.length ? (
                  <Stack
                    component="span"
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    maxWidth="100%"
                    overflow="hidden"
                  >
                    {currentValues.map((value, index) => (
                      <Chip
                        key={`${value}-${index}`}
                        label={value}
                        size="small"
                        onClick={event => event.stopPropagation()}
                        onMouseDown={event => event.stopPropagation()}
                        onDelete={() =>
                          setValues(currentValues.filter((_, valueIndex) => valueIndex !== index))
                        }
                      />
                    ))}
                  </Stack>
                ) : undefined,
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'center', ml: 'auto' }}>
                    <IconButton
                      edge="end"
                      aria-label="Select date value"
                      onClick={event => {
                        event.stopPropagation();
                        openPopover();
                      }}
                      size="small"
                    >
                      <EventIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Popover
              open={isPopoverOpen}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Stack spacing={2} width={420} p={2}>
                <Tabs value={activeTab} onChange={(_, value: DateRuleTab) => setActiveTab(value)}>
                  <Tab label="Fixed" value="fixed" />
                  <Tab label="Custom" value="dynamic" />
                </Tabs>

                {activeTab === 'fixed' ? (
                  <Box>
                    {includeTime ? (
                      <FormDateTimePicker<FixedDateFormData>
                        form={fixedDateForm}
                        fieldName="fixedDateValue"
                        label=""
                      />
                    ) : (
                      <FormDatePicker<FixedDateFormData>
                        form={fixedDateForm}
                        fieldName="fixedDateValue"
                        label=""
                      />
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Grid container spacing={1} mt={0.25}>
                      <Grid item xs={4}>
                        <TextField
                          type="number"
                          margin="dense"
                          fullWidth
                          value={customAmount}
                          onChange={event =>
                            setCustomAmount(Math.max(1, Number(event.target.value) || 1))
                          }
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          select
                          margin="dense"
                          fullWidth
                          value={customUnit}
                          onChange={event => setCustomUnit(event.target.value as CustomDateUnit)}
                        >
                          {customDateUnitOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          select
                          margin="dense"
                          fullWidth
                          value={customDirection}
                          onChange={event =>
                            setCustomDirection(event.target.value as CustomDateDirection)
                          }
                        >
                          {customDateDirectionOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>

                    <Box mt={1.5}>
                      {isDynamicPreviewLoading ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">
                            Calculating preview...
                          </Typography>
                        </Stack>
                      ) : dynamicPreviewError ? (
                        <Typography variant="caption" color="error.main">
                          {dynamicPreviewError}
                        </Typography>
                      ) : dynamicPreviewDate ? (
                        <Typography variant="caption" color="text.secondary">
                          Preview:{' '}
                          {dayjs(dynamicPreviewDate).format(
                            includeTime ? DATETIME_DISPLAY_FORMAT : DATE_DISPLAY_FORMAT
                          )}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                )}

                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button onClick={() => setAnchorEl(null)}>Cancel</Button>
                  <Button variant="contained" onClick={addValueFromSelector}>
                    Add value
                  </Button>
                </Stack>
              </Stack>
            </Popover>
          </Box>
        );
      }}
    />
  );
};
